import { useEffect, useRef } from 'react'
import { getSession } from 'utils/api'
import { useAuth } from 'services/auth'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import useCellVisualizationUrlParams from '../useCellVisualizationUrlParams'
import { getCellInfoToGroupsFromDataFrame } from '../utils'
import getSessionData from '../arrow/getSessionData'
import { createDataFrame } from '../arrow/arqueroHelpers'

/**
 * Loads a session's cells into the shared Cell Vis store for the labeling view.
 *
 * This mirrors the data-loading part of useLoadCellInfoEffect but deliberately
 * omits its updateVersion() call. That call rewrites the URL to the versioned
 * session path, which would navigate the user off the /label route. Migration
 * is also skipped since labeling does not use morphotypes.
 */
export const useLoadSessionForLabeling = (onError: (error: unknown) => void): void => {
  const { sessionId, versionId } = useCellVisualizationUrlParams()
  const { setStore, setSessionData } = useCellVisualizationsSlice()
  const { isAuthenticated } = useAuth()

  const loaded = useRef(false)
  const loading = useRef(false)

  useEffect(() => {
    if (!sessionId || !isAuthenticated || loaded.current || loading.current) return

    loading.current = true

    const load = async () => {
      try {
        const {
          status: sessionStatus,
          data: { data: sessionData },
        } = await getSession(sessionId, versionId)

        const { arrowBuffer } = await getSessionData({ sessionId, sessionData, sessionStatus })

        if (arrowBuffer && sessionData) {
          const dataFrame = createDataFrame(arrowBuffer)
          loaded.current = true

          const { version_config: state, create_spec: createSpec } = sessionData
          setStore({
            ...(state ?? {}),
            ...createSpec,
            dataFrame,
            cellInfoGroups: getCellInfoToGroupsFromDataFrame(dataFrame, state?.cellInfoGroups),
          })

          const filteredSessionData = { ...sessionData }
          delete filteredSessionData.version_config
          setSessionData(filteredSessionData)
        }
      } catch (error) {
        onError(error)
      } finally {
        loading.current = false
      }
    }

    load()
  }, [sessionId, versionId, isAuthenticated, setStore, setSessionData, onError])

  // Allow a reload if the session in the URL changes
  useEffect(() => {
    loaded.current = false
    loading.current = false
  }, [sessionId])
}

export default useLoadSessionForLabeling
